// The Conflui server's own health on the System overview (moved from the former System info page): NextAdmin
// `profile` definition list in a `Card`.
import type React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Card, Skeleton } from '../../../components/ui/index.js';
import { getSystemHealthOptions } from '../../../generated/api/@tanstack/react-query.gen.js';
import type { SystemHealth } from '../../../generated/api/types.gen.js';
import { formatUptime } from './metric-format.js';

/** `/health` answers 503 with the same body when a dependency is down; the query throws that body. */
function isSystemHealth(value: unknown): value is SystemHealth {
  return typeof value === 'object' && value !== null && 'checks' in value && 'status' in value;
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between border-b py-2.5 last:border-0">
      <dt className="text-sm text-muted">{label}</dt>
      <dd className="text-sm font-medium text-heading">{children}</dd>
    </div>
  );
}

function Check({ status, latencyMs }: { status: string; latencyMs?: number }) {
  return (
    <span className="flex items-center gap-2">
      {latencyMs !== undefined && <span className="text-xs font-normal text-muted">{latencyMs} ms</span>}
      <Badge variant={status === 'healthy' ? 'success' : 'danger'} dot>
        {status === 'healthy' ? 'Connected' : 'Disconnected'}
      </Badge>
    </span>
  );
}

export function ControlPlaneCard() {
  const query = useQuery({ ...getSystemHealthOptions(), refetchInterval: 30_000 });
  const health: SystemHealth | undefined = query.data ?? (isSystemHealth(query.error) ? query.error : undefined);
  return (
    <Card title="This server" className="xl:col-span-12">
      {health ? (
        <dl>
          <Row label="Status">
            <Badge variant={health.status === 'healthy' ? 'success' : 'danger'} dot>
              {health.status === 'healthy' ? 'Healthy' : 'Degraded'}
            </Badge>
          </Row>
          <Row label="Up for">{formatUptime(health.uptime)}</Row>
          <Row label="Environment">{health.environment}</Row>
          <Row label="PostgreSQL">
            <Check status={health.checks.database.status} latencyMs={health.checks.database.latency_ms} />
          </Row>
          <Row label="Redis">
            <Check status={health.checks.redis.status} latencyMs={health.checks.redis.latency_ms} />
          </Row>
        </dl>
      ) : query.isError ? (
        <p className="text-sm text-danger">The server did not answer its health check.</p>
      ) : (
        <Skeleton height={160} />
      )}
    </Card>
  );
}
